import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';
import { ANALYTICS_PERIOD_KIND, type AnalyticsReportSummary } from './analyticsTypes';

export interface LabelledReport {
  summary: AnalyticsReportSummary;
  label: string;
}

interface ReportListProps {
  reports: readonly LabelledReport[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Present only when the caller may delete ad-hoc reports. */
  onDelete?: (report: LabelledReport) => void;
  /** A delete is in flight: every delete control is disabled. */
  deleting?: boolean;
}

function groupsOf(reports: readonly LabelledReport[]) {
  return [
    {
      key: ANALYTICS_PERIOD_KIND.weekly,
      title: 'analytics.list.weekly',
      items: reports.filter(
        (report) => report.summary.period_kind === ANALYTICS_PERIOD_KIND.weekly,
      ),
    },
    {
      key: ANALYTICS_PERIOD_KIND.adhoc,
      title: 'analytics.list.adhoc',
      items: reports.filter((report) => report.summary.period_kind === ANALYTICS_PERIOD_KIND.adhoc),
    },
  ] as const;
}

/**
 * The report archive: a sticky rail on wide screens, a select below `lg`.
 * Both name reports by their readable label — the id is never shown.
 */
export function ReportList({
  reports,
  selectedId,
  onSelect,
  onDelete,
  deleting = false,
}: ReportListProps) {
  const { t, i18n } = useTranslation();
  const groups = groupsOf(reports).filter((group) => group.items.length > 0);
  const generatedOn = new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' });

  return (
    <>
      <div className="lg:hidden">
        <Select
          value={selectedId ?? ''}
          onValueChange={(value) => {
            if (typeof value === 'string' && value !== '') {
              onSelect(value);
            }
          }}
          // `items` feeds Select.Value the label; without it the trigger shows the raw id.
          items={Object.fromEntries(reports.map((report) => [report.summary.id, report.label]))}
        >
          <SelectTrigger aria-label={t('analytics.list.label')} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) => (
              <SelectGroup key={group.key}>
                <SelectLabel>{t(group.title)}</SelectLabel>
                {group.items.map((report) => (
                  <SelectItem key={report.summary.id} value={report.summary.id}>
                    {report.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav
        aria-label={t('analytics.list.label')}
        className="hidden flex-col gap-5 lg:sticky lg:top-4 lg:flex"
      >
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-1">
            <p className="px-3 text-xs font-medium text-muted-foreground">{t(group.title)}</p>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((report) => {
                const selected = report.summary.id === selectedId;
                const dateId = `analytics-report-${report.summary.id}-generated`;
                const deletable =
                  onDelete !== undefined &&
                  report.summary.period_kind === ANALYTICS_PERIOD_KIND.adhoc;
                return (
                  <li key={report.summary.id} className="group relative">
                    <button
                      type="button"
                      aria-label={report.label}
                      aria-describedby={dateId}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => onSelect(report.summary.id)}
                      className={cn(
                        'flex w-full flex-col gap-0.5 rounded-md border-l-2 py-2 pl-3 text-left transition-colors',
                        deletable ? 'pr-10' : 'pr-3',
                        selected
                          ? 'border-primary bg-muted text-foreground'
                          : 'border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                      )}
                    >
                      <span className={cn('text-sm', selected && 'font-medium')}>
                        {report.label}
                      </span>
                      <span id={dateId} className="text-xs text-muted-foreground">
                        {t('analytics.list.generatedOn', {
                          date: generatedOn.format(new Date(report.summary.generated_at)),
                        })}
                      </span>
                    </button>
                    {deletable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('analytics.delete.ariaLabel', { label: report.label })}
                        className="absolute top-1/2 right-1 size-8 -translate-y-1/2 text-muted-foreground opacity-60 group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100"
                        disabled={deleting}
                        onClick={() => onDelete(report)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}
